import React, { useState, useEffect, useCallback } from 'react';
import { solicitudesAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Plus, Edit, Trash2, Loader2, GripVertical, X, FileText,
    Globe, Lock, MapPin, ChevronDown, ChevronUp, Settings
} from 'lucide-react';

const FIELD_TYPES = [
    { value: 'text', label: 'Texto' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Fecha' },
    { value: 'time', label: 'Hora' },
    { value: 'textarea', label: 'Área de texto' },
    { value: 'select', label: 'Lista desplegable' },
];

const emptyTipo = {
    nombre: '',
    descripcion: '',
    slug: '',
    es_publico: true,
    requiere_gerencia_area: false,
    campos: [],
    documentos_obligatorios: [],
    gerencias_area: [],
    motivos_rechazo: [],
};

const emptyCampo = { key: '', label: '', tipo: 'text', requerido: false, opciones: [] };
const emptyDoc = { key: '', label: '', requerido: true };

export default function TiposSolicitudAdminPage() {
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ ...emptyTipo });
    const [saving, setSaving] = useState(false);
    const [expandedSection, setExpandedSection] = useState('campos');

    const loadTipos = useCallback(async () => {
        try {
            const r = await solicitudesAPI.getTipos();
            setTipos(r.data);
        } catch { /* */ }
        setLoading(false);
    }, []);

    useEffect(() => { loadTipos(); }, [loadTipos]);

    const autoSlug = (nombre) => nombre.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    const openNew = () => {
        setEditId(null);
        setForm({ ...emptyTipo });
        setShowDialog(true);
    };

    const openEdit = (tipo) => {
        setEditId(tipo.id);
        setForm({
            nombre: tipo.nombre || '',
            descripcion: tipo.descripcion || '',
            slug: tipo.slug || '',
            es_publico: tipo.es_publico ?? true,
            requiere_gerencia_area: tipo.requiere_gerencia_area ?? false,
            campos: tipo.campos || [],
            documentos_obligatorios: tipo.documentos_obligatorios || [],
            gerencias_area: tipo.gerencias_area || [],
            motivos_rechazo: tipo.motivos_rechazo || [],
        });
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!form.nombre.trim()) return toast.error('Nombre es requerido');
        if (form.campos.some(c => !c.key || !c.label)) return toast.error('Todos los campos deben tener Key y Label');
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug || autoSlug(form.nombre),
            };
            if (editId) {
                await solicitudesAPI.updateTipo(editId, payload);
                toast.success('Tipo actualizado');
            } else {
                await solicitudesAPI.createTipo(payload);
                toast.success('Tipo creado');
            }
            setShowDialog(false);
            loadTipos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        }
        setSaving(false);
    };

    const handleDelete = async (tipo) => {
        if (!window.confirm(`¿Desactivar "${tipo.nombre}"?`)) return;
        try {
            await solicitudesAPI.deleteTipo(tipo.id);
            toast.success('Tipo desactivado');
            loadTipos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        }
    };

    // ---- Campo helpers ----
    const addCampo = () => setForm(f => ({ ...f, campos: [...f.campos, { ...emptyCampo, key: `campo_${f.campos.length + 1}` }] }));
    const removeCampo = (i) => setForm(f => ({ ...f, campos: f.campos.filter((_, idx) => idx !== i) }));
    const updateCampo = (i, field, val) => {
        setForm(f => {
            const c = [...f.campos];
            c[i] = { ...c[i], [field]: val };
            if (field === 'label' && !editId) {
                c[i].key = c[i].label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
            }
            return { ...f, campos: c };
        });
    };

    // ---- Doc helpers ----
    const addDoc = () => setForm(f => ({ ...f, documentos_obligatorios: [...f.documentos_obligatorios, { ...emptyDoc, key: `doc_${f.documentos_obligatorios.length + 1}` }] }));
    const removeDoc = (i) => setForm(f => ({ ...f, documentos_obligatorios: f.documentos_obligatorios.filter((_, idx) => idx !== i) }));
    const updateDoc = (i, field, val) => {
        setForm(f => {
            const d = [...f.documentos_obligatorios];
            d[i] = { ...d[i], [field]: val };
            if (field === 'label' && !editId) {
                d[i].key = d[i].label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
            }
            return { ...f, documentos_obligatorios: d };
        });
    };

    // ---- Gerencia / Motivo helpers (simple string lists) ----
    const addToList = (listKey) => setForm(f => ({ ...f, [listKey]: [...f[listKey], ''] }));
    const removeFromList = (listKey, i) => setForm(f => ({ ...f, [listKey]: f[listKey].filter((_, idx) => idx !== i) }));
    const updateInList = (listKey, i, val) => setForm(f => {
        const arr = [...f[listKey]];
        arr[i] = val;
        return { ...f, [listKey]: arr };
    });

    const toggleSection = (s) => setExpandedSection(prev => prev === s ? null : s);

    if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Layout>;

    return (
        <Layout>
        <div className="space-y-4" data-testid="tipos-solicitud-admin">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Tipos de Solicitud</h1>
                <Button onClick={openNew} data-testid="btn-nuevo-tipo">
                    <Plus className="h-4 w-4 mr-2" /> Nuevo Tipo
                </Button>
            </div>

            {tipos.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                        <p>No hay tipos de solicitud configurados</p>
                        <Button className="mt-4" onClick={openNew}>Crear el primero</Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-3">
                    {tipos.map(tipo => (
                        <Card key={tipo.id} className="hover:border-primary/30 transition-colors">
                            <CardContent className="py-4 flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-sm">{tipo.nombre}</h3>
                                        <Badge variant="outline" className="text-xs">
                                            {tipo.es_publico ? <><Globe className="h-3 w-3 mr-1" /> Publico</> : <><Lock className="h-3 w-3 mr-1" /> Privado</>}
                                        </Badge>
                                        {tipo.requiere_gerencia_area && (
                                            <Badge variant="outline" className="text-xs"><MapPin className="h-3 w-3 mr-1" /> Geo</Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">{tipo.descripcion || 'Sin descripcion'}</p>
                                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                                        <span>{tipo.campos?.length || 0} campos</span>
                                        <span>{tipo.documentos_obligatorios?.length || 0} docs</span>
                                        <span>slug: /{tipo.slug}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => openEdit(tipo)} data-testid={`edit-tipo-${tipo.id}`}>
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(tipo)} data-testid={`delete-tipo-${tipo.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Dialog Crear/Editar */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Editar Tipo' : 'Nuevo Tipo de Solicitud'}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Info básica */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Nombre *</Label>
                                <Input value={form.nombre} onChange={e => {
                                    const nombre = e.target.value;
                                    setForm(f => ({ ...f, nombre, slug: editId ? f.slug : autoSlug(nombre) }));
                                }} data-testid="tipo-nombre" placeholder="Ej: Solicitud de Vuelo de Dron" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Slug (URL)</Label>
                                <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} data-testid="tipo-slug" placeholder="vuelo-rpas" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Descripcion</Label>
                            <Input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} data-testid="tipo-descripcion" />
                        </div>
                        <div className="flex gap-6">
                            <div className="flex items-center gap-2">
                                <Checkbox id="es_publico" checked={form.es_publico} onCheckedChange={v => setForm(f => ({ ...f, es_publico: v }))} data-testid="tipo-es-publico" />
                                <label htmlFor="es_publico" className="text-sm cursor-pointer">Formulario publico (sin login)</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox id="req_gerencia" checked={form.requiere_gerencia_area} onCheckedChange={v => setForm(f => ({ ...f, requiere_gerencia_area: v }))} data-testid="tipo-req-gerencia" />
                                <label htmlFor="req_gerencia" className="text-sm cursor-pointer">Requiere Gerencia de Area</label>
                            </div>
                        </div>

                        {/* Campos dinámicos */}
                        <div className="border rounded-lg">
                            <button className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50"
                                onClick={() => toggleSection('campos')} type="button">
                                <span>Campos del Formulario ({form.campos.length})</span>
                                {expandedSection === 'campos' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            {expandedSection === 'campos' && (
                                <div className="p-3 pt-0 space-y-2">
                                    {form.campos.map((campo, i) => (
                                        <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-center border rounded p-2 bg-muted/20">
                                            <Input placeholder="Label" value={campo.label} onChange={e => updateCampo(i, 'label', e.target.value)} className="h-8 text-xs" />
                                            {React.createElement('select', {
                                                className: "h-8 text-xs border rounded px-2 bg-background",
                                                value: campo.tipo,
                                                onChange: e => updateCampo(i, 'tipo', e.target.value),
                                            }, FIELD_TYPES.map(t => React.createElement('option', { key: t.value, value: t.value }, t.label)))}
                                            <div className="flex items-center gap-1">
                                                <Checkbox checked={campo.requerido} onCheckedChange={v => updateCampo(i, 'requerido', v)} />
                                                <span className="text-xs">Req</span>
                                            </div>
                                            {campo.tipo === 'select' && (
                                                <Input placeholder="Opciones (sep. por coma)" className="h-8 text-xs col-span-1"
                                                    value={(campo.opciones || []).join(', ')}
                                                    onChange={e => updateCampo(i, 'opciones', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                                            )}
                                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeCampo(i)}><X className="h-3 w-3" /></Button>
                                        </div>
                                    ))}
                                    <Button size="sm" variant="outline" onClick={addCampo} className="w-full" data-testid="btn-add-campo">
                                        <Plus className="h-3 w-3 mr-1" /> Agregar Campo
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Documentos obligatorios */}
                        <div className="border rounded-lg">
                            <button className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50"
                                onClick={() => toggleSection('docs')} type="button">
                                <span>Documentos Obligatorios ({form.documentos_obligatorios.length})</span>
                                {expandedSection === 'docs' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            {expandedSection === 'docs' && (
                                <div className="p-3 pt-0 space-y-2">
                                    {form.documentos_obligatorios.map((doc, i) => (
                                        <div key={i} className="flex gap-2 items-center border rounded p-2 bg-muted/20">
                                            <Input placeholder="Label (ej: Matrícula DGAC)" value={doc.label} onChange={e => updateDoc(i, 'label', e.target.value)} className="h-8 text-xs flex-1" />
                                            <div className="flex items-center gap-1">
                                                <Checkbox checked={doc.requerido} onCheckedChange={v => updateDoc(i, 'requerido', v)} />
                                                <span className="text-xs">Req</span>
                                            </div>
                                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeDoc(i)}><X className="h-3 w-3" /></Button>
                                        </div>
                                    ))}
                                    <Button size="sm" variant="outline" onClick={addDoc} className="w-full" data-testid="btn-add-doc">
                                        <Plus className="h-3 w-3 mr-1" /> Agregar Documento
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Gerencias de Área */}
                        <div className="border rounded-lg">
                            <button className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50"
                                onClick={() => toggleSection('gerencias')} type="button">
                                <span>Gerencias de Area ({form.gerencias_area.length})</span>
                                {expandedSection === 'gerencias' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            {expandedSection === 'gerencias' && (
                                <div className="p-3 pt-0 space-y-2">
                                    {form.gerencias_area.map((g, i) => (
                                        <div key={i} className="flex gap-2 items-center">
                                            <Input placeholder="Nombre del area" value={g} onChange={e => updateInList('gerencias_area', i, e.target.value)} className="h-8 text-xs flex-1" />
                                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeFromList('gerencias_area', i)}><X className="h-3 w-3" /></Button>
                                        </div>
                                    ))}
                                    <Button size="sm" variant="outline" onClick={() => addToList('gerencias_area')} className="w-full" data-testid="btn-add-gerencia">
                                        <Plus className="h-3 w-3 mr-1" /> Agregar Area
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Motivos de Rechazo */}
                        <div className="border rounded-lg">
                            <button className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50"
                                onClick={() => toggleSection('motivos')} type="button">
                                <span>Motivos de Rechazo ({form.motivos_rechazo.length})</span>
                                {expandedSection === 'motivos' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                            {expandedSection === 'motivos' && (
                                <div className="p-3 pt-0 space-y-2">
                                    {form.motivos_rechazo.map((m, i) => (
                                        <div key={i} className="flex gap-2 items-center">
                                            <Input placeholder="Motivo" value={m} onChange={e => updateInList('motivos_rechazo', i, e.target.value)} className="h-8 text-xs flex-1" />
                                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeFromList('motivos_rechazo', i)}><X className="h-3 w-3" /></Button>
                                        </div>
                                    ))}
                                    <Button size="sm" variant="outline" onClick={() => addToList('motivos_rechazo')} className="w-full" data-testid="btn-add-motivo">
                                        <Plus className="h-3 w-3 mr-1" /> Agregar Motivo
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="btn-guardar-tipo">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            {editId ? 'Actualizar' : 'Crear Tipo'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </Layout>
    );
}
